/** SUNTRONIX ONE — Tenant Catalogue (customer portal).
 *  Governed B2B catalogue: visibility-scoped products with tenant pricing (or
 *  Request Quote), Product-Intelligence detail, Cart, Quick Order, Compare and
 *  Request Formal Quotation (→ Sales Desk enquiry → STX quote). */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import {
  Search, PackageSearch, Cpu, ShieldCheck, Loader2, ChevronRight, Lock, Store,
  ShoppingCart, Plus, Minus, Trash2, Scale, ListPlus, Send, Check, X,
  Heart, FileText, Puzzle, Bookmark, RotateCcw, CalendarIcon, FileUp, Sparkles,
  ArrowRight, Wallet, Workflow, Recycle,
} from "lucide-react";

const money = (n, cur = "INR") =>
  n == null ? "—" : `${cur === "INR" ? "₹" : cur + " "}${Number(n).toLocaleString("en-IN")}`;

// Shadcn calendar date picker (stores ISO yyyy-MM-dd, disallows past dates)
function DateField({ value, onChange, testid }) {
  const selected = value ? parseISO(value) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={testid}
          className={`h-9 mt-1 w-full justify-start text-left font-normal ${value ? "text-slate-900" : "text-slate-400"}`}
        >
          <CalendarIcon className="h-4 w-4 mr-2 text-slate-400" />
          {value ? format(selected, "dd MMM yyyy") : "Select a date"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")}
          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

const PriceTag = ({ pricing }) =>
  pricing?.mode === "PRICE"
    ? <span className="text-base font-semibold text-slate-900">{money(pricing.unit_price, pricing.currency)}<span className="text-[11px] font-normal text-slate-400"> + GST</span></span>
    : <Badge className="bg-amber-50 text-amber-700" data-testid="rfq-badge">Request Quote</Badge>;

// ---------------------------------------------------------------- product detail
function ProductDetail({ code, onClose, onAdd }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/tenant-catalogue/products/${code}`).then(({ data }) => setP(data))
      .catch((e) => setErr(e.response?.data?.detail || "Not available"));
  }, [code]);
  const spec = p?.intelligence?.normalised || {};
  const specRows = Object.entries(spec).filter(([, v]) => v && String(v).trim()).slice(0, 16);
  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="tc-product-detail">
        <DialogHeader className={p ? "" : "sr-only"}>
          {p && <div className="text-[10px] uppercase tracking-widest text-slate-400">{p.brand} · {p.category}</div>}
          <DialogTitle>{p ? p.product_name : "Product"}</DialogTitle>
          {p && <div className="text-[12px] font-mono text-slate-400">{p.oem_part_number || p.sku || p.product_code}</div>}
        </DialogHeader>
        {err ? <div className="py-10 text-center text-slate-500">{err}</div> : !p ? (
          <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className="overflow-y-auto flex-1 min-h-0 space-y-4">
            <div className="flex items-start gap-4">
              {p.primary_image ? <img src={p.primary_image} alt="" className="w-32 h-32 object-contain rounded-lg border border-slate-100 bg-white" />
                : <div className="w-32 h-32 rounded-lg border border-slate-100 bg-slate-50 flex items-center justify-center"><PackageSearch className="h-8 w-8 text-slate-300" /></div>}
              <div className="flex-1 space-y-2">
                <PriceTag pricing={p.pricing} />
                {p.warranty && <div className="text-[12px] text-slate-600"><ShieldCheck className="h-3.5 w-3.5 inline mr-1 text-emerald-600" />{p.warranty}</div>}
                {p.commercial_description && <p className="text-[13px] text-slate-600">{p.commercial_description}</p>}
                <Button size="sm" onClick={() => { onAdd(p.product_code); onClose(); }} data-testid="tc-detail-add" className="bg-sky-600 hover:bg-sky-700 text-white"><ShoppingCart className="h-3.5 w-3.5 mr-1.5" />Add to cart</Button>
              </div>
            </div>
            {specRows.length > 0 && (
              <div className="rounded-lg border border-slate-200 p-4" data-testid="tc-specs">
                <div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 mb-2"><Cpu className="h-4 w-4 text-sky-600" />Key specifications</div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {specRows.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3 text-[12px] border-b border-slate-50 py-1">
                      <span className="text-slate-400 capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="text-slate-700 text-right">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {p.documents?.length > 0 && (
              <div className="rounded-lg border border-slate-200 p-4" data-testid="tc-documents">
                <div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 mb-2"><FileText className="h-4 w-4 text-sky-600" />Documents & datasheets</div>
                <div className="flex flex-wrap gap-2">
                  {p.documents.map((d, i) => (
                    <a key={i} href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] text-sky-700 border border-sky-200 bg-sky-50 rounded-md px-2.5 py-1 hover:bg-sky-100">
                      <FileText className="h-3.5 w-3.5" />{d.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
            {p.accessories?.length > 0 && (
              <div className="rounded-lg border border-slate-200 p-4" data-testid="tc-accessories">
                <div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 mb-2"><Puzzle className="h-4 w-4 text-sky-600" />Compatible accessories</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {p.accessories.map((a) => (
                    <div key={a.product_code} data-testid={`tc-accessory-${a.product_code}`} className="border border-slate-100 rounded-lg p-2 flex flex-col">
                      <div className="h-14 flex items-center justify-center mb-1">
                        {a.primary_image ? <img src={a.primary_image} alt="" className="max-h-14 object-contain" /> : <PackageSearch className="h-6 w-6 text-slate-200" />}
                      </div>
                      <div className="text-[11px] font-medium text-slate-700 line-clamp-2 min-h-[2rem]">{a.product_name}</div>
                      <div className="flex items-center justify-between mt-1">
                        <PriceTag pricing={a.pricing} />
                        <button onClick={() => onAdd(a.product_code)} title="Add" className="h-6 w-6 rounded bg-sky-600 hover:bg-sky-700 text-white flex items-center justify-center"><ShoppingCart className="h-3 w-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- compare
function CompareDialog({ codes, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.post("/tenant-catalogue/compare", { product_codes: codes })
      .then(({ data }) => setData(data)).catch((e) => setErr(e.response?.data?.detail || "Compare failed"));
  }, [codes]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="tc-compare-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Scale className="h-5 w-5 text-sky-600" />Compare products</DialogTitle></DialogHeader>
        {err ? <div className="py-10 text-center text-slate-500">{err}</div> : !data ? (
          <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full text-[12px]">
              <thead>
                <tr>
                  <th className="text-left p-2 text-slate-400 font-medium sticky left-0 bg-white">Attribute</th>
                  {data.products.map((p) => (
                    <th key={p.product_code} className="text-left p-2 min-w-[160px]">
                      {p.primary_image && <img src={p.primary_image} alt="" className="h-16 object-contain mb-1" />}
                      <div className="font-semibold text-slate-800">{p.product_name}</div>
                      <div className="text-[10px] font-mono text-slate-400">{p.part_number || p.product_code}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-slate-100"><td className="p-2 text-slate-400 sticky left-0 bg-white">Price</td>
                  {data.products.map((p) => <td key={p.product_code} className="p-2"><PriceTag pricing={p.pricing} /></td>)}</tr>
                <tr className="border-t border-slate-100"><td className="p-2 text-slate-400 sticky left-0 bg-white">Warranty</td>
                  {data.products.map((p) => <td key={p.product_code} className="p-2 text-slate-700">{p.warranty || "—"}</td>)}</tr>
                {data.spec_keys.length === 0 && (
                  <tr className="border-t border-slate-100"><td colSpan={data.products.length + 1} className="p-4 text-center text-slate-400">No detailed specifications available for these products.</td></tr>
                )}
                {data.spec_keys.map((k) => (
                  <tr key={k} className="border-t border-slate-100">
                    <td className="p-2 text-slate-400 capitalize sticky left-0 bg-white">{k.replace(/_/g, " ")}</td>
                    {data.products.map((p) => <td key={p.product_code} className="p-2 text-slate-700">{p.specs?.[k] ? String(p.specs[k]) : "—"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- quick order
function QuickOrderDialog({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/tenant-catalogue/cart/quick-order", { text }); setResult(data); onDone(data.cart); }
    catch (e) { toast.error(e.response?.data?.detail || "Quick order failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="tc-quick-order">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ListPlus className="h-5 w-5 text-sky-600" />Quick Order</DialogTitle></DialogHeader>
        <p className="text-[12px] text-slate-500">Paste part numbers with quantities, one per line. Example: <span className="font-mono">21QVS18E00 10</span></p>
        <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} data-testid="qo-text" className="text-sm font-mono" placeholder={"MDH84HN/A 5\nSCW83HN/A 3"} />
        <Button onClick={submit} disabled={busy || !text.trim()} data-testid="qo-submit" className="bg-sky-600 hover:bg-sky-700 text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ListPlus className="h-4 w-4 mr-2" />}Add to cart
        </Button>
        {result && (
          <div className="text-[12px] space-y-1" data-testid="qo-result">
            <div className="text-emerald-700">{result.matched.length} added to cart.</div>
            {result.unmatched.length > 0 && <div className="text-amber-700">{result.unmatched.length} not found: {result.unmatched.map((u) => u.token).join(", ")}</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- AI PO import
function PoImportDialog({ onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const extract = async () => {
    if (!file) { toast.error("Choose a PO file (PDF or image)"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/tenant-catalogue/cart/import-po", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (!data.matched?.length) toast.warning("No catalogue matches found in this PO");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the PO"); }
    finally { setBusy(false); }
  };
  const addToCart = async () => {
    setAdding(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/tenant-catalogue/cart/import-po?add_to_cart=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.matched.length} line(s) added to cart`); onDone(data.cart); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add to cart"); }
    finally { setAdding(false); }
  };
  const m = result?.po_meta || {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid="tc-po-import">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileUp className="h-5 w-5 text-sky-600" />Import Customer PO</DialogTitle></DialogHeader>
        <p className="text-[12px] text-slate-500">Upload your Purchase Order (PDF or image). We use AI to read the PO number, delivery details and line items, then match parts to your catalogue.</p>
        <div className="space-y-3 overflow-y-auto">
          <label className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-4 cursor-pointer hover:border-sky-400" data-testid="po-file-label">
            <FileUp className="h-4 w-4 text-slate-400" />
            <span className="text-[13px] text-slate-600 truncate">{file ? file.name : "Choose a PO file…"}</span>
            <input type="file" accept=".pdf,image/*" className="hidden" data-testid="po-file" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
          </label>
          <Button onClick={extract} disabled={busy || !file} data-testid="po-extract" className="w-full bg-sky-600 hover:bg-sky-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}{busy ? "Reading PO…" : "Extract with AI"}
          </Button>
          {result && (
            <div className="space-y-3" data-testid="po-result">
              {m.po_number && (
                <div className="rounded-lg bg-slate-50 p-3 text-[12px] space-y-0.5">
                  <div className="font-medium text-slate-800">PO {m.po_number}{m.po_date ? ` · ${m.po_date}` : ""}</div>
                  {m.ship_to && <div className="text-slate-600">Ship to: {m.ship_to}</div>}
                  {m.required_by && <div className="text-slate-600">Required by: {m.required_by}</div>}
                  {m.contact_name && <div className="text-slate-600">Contact: {m.contact_name}{m.contact_phone ? ` · ${m.contact_phone}` : ""}</div>}
                </div>
              )}
              <div>
                <div className="text-[12px] font-semibold text-emerald-700 mb-1">{result.matched.length} matched line(s)</div>
                {result.matched.map((x, i) => (
                  <div key={i} data-testid={`po-matched-${i}`} className="flex items-center gap-2 text-[12px] py-1 border-b border-slate-50">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span className="font-mono text-slate-500">{x.token}</span>
                    <span className="text-slate-700 truncate flex-1">{x.product_name}</span>
                    <span className="text-slate-400">×{x.quantity}</span>
                  </div>
                ))}
                {result.matched.length === 0 && <div className="text-[12px] text-slate-400">No parts matched your catalogue.</div>}
              </div>
              {result.unmatched.length > 0 && (
                <div>
                  <div className="text-[12px] font-semibold text-amber-700 mb-1">{result.unmatched.length} not found</div>
                  {result.unmatched.map((u, i) => (
                    <div key={i} className="flex items-center gap-2 text-[12px] py-0.5 text-slate-500">
                      <X className="h-3.5 w-3.5 text-amber-500 shrink-0" /><span className="font-mono">{u.token || "—"}</span><span className="truncate">{u.description}</span><span>×{u.quantity}</span>
                    </div>
                  ))}
                  <p className="text-[11px] text-slate-400 mt-1">Unmatched lines can be requested via a formal quotation.</p>
                </div>
              )}
              {result.matched.length > 0 && (
                <Button onClick={addToCart} disabled={adding} data-testid="po-add-cart" className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                  {adding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShoppingCart className="h-4 w-4 mr-2" />}Add {result.matched.length} matched to cart
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- RFQ
function RfqDialog({ onClose, onDone }) {
  const [f, setF] = useState({ delivery_location: "", required_by: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/tenant-catalogue/cart/request-quote", f); toast.success(`Request submitted — ${data.enquiry_no}`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Request failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="tc-rfq-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-sky-600" />Request Formal Quotation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-[12px] text-slate-600">Delivery location</label><Input value={f.delivery_location} onChange={(e) => setF({ ...f, delivery_location: e.target.value })} data-testid="rfq-location" className="h-9 mt-1" /></div>
          <div><label className="text-[12px] text-slate-600">Required by</label><DateField value={f.required_by} onChange={(v) => setF({ ...f, required_by: v })} testid="rfq-date" /></div>
          <div><label className="text-[12px] text-slate-600">Notes</label><Textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="rfq-notes" className="text-sm mt-1" /></div>
          <Button onClick={submit} disabled={busy} data-testid="rfq-submit" className="w-full bg-sky-600 hover:bg-sky-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Submit request
          </Button>
          <p className="text-[11px] text-slate-400">Our Sales Desk will prepare a formal SUNTRONIX quotation and it will appear in My Quotes.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- checkout (direct order)
function CheckoutDialog({ poRequired, onClose, onDone }) {
  const [f, setF] = useState({ ship_to: "", contact_name: "", contact_phone: "", po_number: "", required_by: "", cost_centre: "", notes: "" });
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/tenant-catalogue/locations").then(({ data }) => setLocations(data.items || [])).catch(() => {}); }, []);
  const submit = async () => {
    if (!f.ship_to.trim()) { toast.error("Enter a delivery location"); return; }
    if (poRequired && !f.po_number.trim()) { toast.error("A Customer PO number is required"); return; }
    setBusy(true);
    try { const { data } = await api.post("/tenant-catalogue/cart/place-order", f);
      toast.success(data.needs_approval ? `Order ${data.order_no} submitted for approval` : `Order placed — ${data.order_no}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not place order"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col" data-testid="tc-checkout-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-sky-600" />Place Order</DialogTitle></DialogHeader>
        <div className="space-y-3 overflow-y-auto">
          <div><label className="text-[12px] text-slate-600">Ship-to location *</label>
            {locations.length > 0 ? (
              <select value={f.ship_to} onChange={(e) => setF({ ...f, ship_to: e.target.value })} data-testid="co-ship-to" className="w-full h-9 rounded-md border border-slate-200 text-sm px-2 mt-1">
                <option value="">Select an address…</option>
                {locations.map((l) => { const v = `${l.label} — ${l.address}${l.city ? ", " + l.city : ""}`; return <option key={l.id} value={v}>{v}</option>; })}
              </select>
            ) : <Input value={f.ship_to} onChange={(e) => setF({ ...f, ship_to: e.target.value })} data-testid="co-ship-to" className="h-9 mt-1" placeholder="Delivery address" />}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[12px] text-slate-600">Contact</label><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="co-contact" className="h-9 mt-1" /></div>
            <div><label className="text-[12px] text-slate-600">Phone</label><Input value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} className="h-9 mt-1" /></div>
          </div>
          <div><label className="text-[12px] text-slate-600">Customer PO number {poRequired && <span className="text-rose-500">*</span>}</label><Input value={f.po_number} onChange={(e) => setF({ ...f, po_number: e.target.value })} data-testid="co-po" className="h-9 mt-1" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[12px] text-slate-600">Required by</label><DateField value={f.required_by} onChange={(v) => setF({ ...f, required_by: v })} testid="co-required-by" /></div>
            <div><label className="text-[12px] text-slate-600">Cost centre</label><Input value={f.cost_centre} onChange={(e) => setF({ ...f, cost_centre: e.target.value })} className="h-9 mt-1" /></div>
          </div>
          <div><label className="text-[12px] text-slate-600">Notes</label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="text-sm mt-1" /></div>
          <Button onClick={submit} disabled={busy} data-testid="co-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShoppingCart className="h-4 w-4 mr-2" />}Place order
          </Button>
          <p className="text-[11px] text-slate-400">Taxes on the tax invoice. Delivery at actuals. You can upload the PO document in My Orders.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- page
export default function TenantCatalogue() {
  const [settings, setSettings] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [facets, setFacets] = useState({ brands: [], categories: [] });
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [cart, setCart] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [compare, setCompare] = useState([]);        // selected product codes
  const [showCompare, setShowCompare] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const [showPo, setShowPo] = useState(false);
  const [showRfq, setShowRfq] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [savedCarts, setSavedCarts] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const gridRef = useRef(null);
  const searchRef = useRef(null);
  const goToCatalogue = () => {
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => searchRef.current?.focus(), 350);
  };

  const loadCart = useCallback(() => api.get("/tenant-catalogue/cart").then(({ data }) => setCart(data)).catch(() => {}), []);
  const loadWishlist = useCallback(() => api.get("/tenant-catalogue/wishlist").then(({ data }) => setWishlist(data.codes || [])).catch(() => {}), []);
  const loadSavedCarts = useCallback(() => api.get("/tenant-catalogue/saved-carts").then(({ data }) => setSavedCarts(data.items || [])).catch(() => {}), []);

  useEffect(() => {
    api.get("/tenant-catalogue/my-settings").then(({ data }) => {
      setSettings(data);
      if (!data.catalogue_enabled) setBlocked("Catalogue is not enabled for your organisation yet.");
      else { loadCart(); loadWishlist(); loadSavedCarts(); }
    }).catch((e) => setBlocked(e.response?.data?.detail || "Catalogue unavailable"));
    api.get("/tenant-catalogue/facets").then(({ data }) => setFacets(data)).catch(() => {});
  }, [loadCart, loadWishlist, loadSavedCarts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant-catalogue/products", {
        params: { q: q || undefined, brand: brand || undefined, category: category || undefined, limit: 24 },
      });
      setData(data);
    } catch (e) { if (!settings) setBlocked(e.response?.data?.detail || "Catalogue unavailable"); }
    finally { setLoading(false); }
  }, [q, brand, category, settings]);

  useEffect(() => { if (settings?.catalogue_enabled) { const t = setTimeout(load, 250); return () => clearTimeout(t); } }, [load, settings]);

  const addToCart = async (code, qty = 1) => {
    try { const { data } = await api.post("/tenant-catalogue/cart/items", { product_code: code, quantity: qty }); setCart(data); toast.success("Added to cart"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const updateQty = async (code, qty) => {
    if (qty < 1) return removeItem(code);
    const { data } = await api.patch(`/tenant-catalogue/cart/items/${code}`, { product_code: code, quantity: qty }); setCart(data);
  };
  const removeItem = async (code) => { const { data } = await api.delete(`/tenant-catalogue/cart/items/${code}`); setCart(data); };
  const toggleCompare = (code) => setCompare((c) => c.includes(code) ? c.filter((x) => x !== code) : c.length >= 4 ? (toast.error("Compare up to 4 products"), c) : [...c, code]);

  const toggleWishlist = async (code) => {
    try { const { data } = await api.post("/tenant-catalogue/wishlist/toggle", { product_code: code });
      setWishlist((w) => data.saved ? [code, ...w] : w.filter((x) => x !== code));
    } catch { toast.error("Could not update saved products"); }
  };
  const saveCurrentCart = async () => {
    const name = window.prompt("Name this saved cart", "New Joiners – October");
    if (!name) return;
    try { await api.post("/tenant-catalogue/saved-carts", { name }); toast.success("Cart saved"); loadSavedCarts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save cart"); }
  };
  const restoreCart = async (id) => {
    try { const { data } = await api.post(`/tenant-catalogue/saved-carts/${id}/restore`, null, { params: { merge: true } }); setCart(data); toast.success("Cart restored"); }
    catch { toast.error("Could not restore cart"); }
  };
  const deleteSavedCart = async (id) => { await api.delete(`/tenant-catalogue/saved-carts/${id}`); loadSavedCarts(); };

  const cartCount = cart?.count || 0;

  if (blocked) return (
    <div className="max-w-2xl mx-auto p-10 text-center" data-testid="tc-blocked">
      <Lock className="h-10 w-10 mx-auto mb-3 text-slate-300" />
      <h1 className="text-lg font-semibold text-slate-800">Catalogue unavailable</h1>
      <p className="text-sm text-slate-500 mt-1">{blocked}</p>
    </div>
  );

  return (
    <div className="max-w-[1500px] mx-auto p-6 space-y-6" data-testid="tenant-catalogue">
      {/* Utility bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
          {settings?.tenant_name ? `Welcome, ${settings.tenant_name} · Your Catalogue` : "Your Catalogue"}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowPo(true)} data-testid="tc-po-import-btn"><FileUp className="h-4 w-4 mr-1.5" />Import PO</Button>
          <Button variant="outline" size="sm" onClick={() => setShowQuick(true)} data-testid="tc-quick-order-btn"><ListPlus className="h-4 w-4 mr-1.5" />Quick Order</Button>
          <Button variant="outline" size="sm" onClick={() => compare.length >= 2 ? setShowCompare(true) : toast.error("Select 2-4 products to compare")} data-testid="tc-compare-btn"><Scale className="h-4 w-4 mr-1.5" />Compare{compare.length ? ` (${compare.length})` : ""}</Button>
          <Button size="sm" onClick={() => setCartOpen(true)} data-testid="tc-cart-btn" className="bg-slate-900 hover:bg-slate-800 text-white relative"><ShoppingCart className="h-4 w-4 mr-1.5" />Cart{cartCount ? <span className="ml-1.5 bg-sky-500 text-white rounded-full text-[10px] px-1.5">{cartCount}</span> : ""}</Button>
        </div>
      </div>

      {/* Premium hero */}
      <section className="relative overflow-hidden rounded-2xl bg-slate-950 text-white px-6 py-10 sm:px-14 sm:py-20" data-testid="tc-hero">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="relative max-w-3xl">
          <h1 className="font-heading font-semibold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl" data-testid="tc-hero-headline">
            <span className="block">TAKE THE CHASING</span>
            <span className="block">OUT OF IT PURCHASING.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
            Search products. Compare specifications. Access your pricing. Request quotes.
            Place orders. Track everything — from one SUNTRONIX dashboard.
          </p>
          <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row gap-3">
            <Button onClick={goToCatalogue} data-testid="tc-hero-explore"
              className="h-12 px-7 bg-white text-slate-950 hover:bg-slate-100 font-semibold tracking-wide rounded-full">
              EXPLORE CATALOGUE <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button onClick={() => setShowRfq(true)} data-testid="tc-hero-quote" variant="outline"
              className="h-12 px-7 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white font-semibold tracking-wide rounded-full">
              REQUEST A QUOTE
            </Button>
            {settings?.direct_order_enabled && (
              <Button onClick={() => setShowQuick(true)} data-testid="tc-hero-order" variant="outline"
                className="h-12 px-7 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white font-semibold tracking-wide rounded-full">
                PLACE AN ORDER
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Prominent search */}
      <div ref={gridRef} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3 scroll-mt-6">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by product, part number, SKU, model or category..."
              className="pl-11 h-12 text-[15px]" data-testid="tc-search" />
          </div>
          <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="tc-brand" className="h-12 rounded-md border border-slate-200 text-sm px-3 sm:w-44">
            <option value="">All brands</option>
            {facets.brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="tc-category" className="h-12 rounded-md border border-slate-200 text-sm px-3 sm:w-52">
            <option value="">All categories</option>
            {facets.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-slate-400">Try:</span>
          {["MacBook Pro", "ThinkPad", "Surface", "USB-C Dock", "Jabra Headset", "Printer", "Monitor"].map((ex) => (
            <button key={ex} onClick={() => setQ(ex)} data-testid={`tc-example-${ex.replace(/\s+/g, "-").toLowerCase()}`}
              className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors">
              {ex}
            </button>
          ))}
        </div>
      </div>

      {/* Value strip */}
      <div className="text-center py-2" data-testid="tc-value-strip">
        <div className="flex items-center justify-center flex-wrap gap-x-3 gap-y-1 font-heading font-semibold text-slate-900 tracking-tight text-lg sm:text-xl">
          {["SEARCH.", "COMPARE.", "QUOTE.", "ORDER.", "TRACK."].map((w, i) => (
            <span key={w} className={i % 2 ? "text-sky-600" : "text-slate-900"}>{w}</span>
          ))}
        </div>
        <p className="text-sm text-slate-500 mt-1.5 max-w-xl mx-auto">
          Everything your organisation needs to purchase and manage technology — in one place.
        </p>
      </div>

      {/* Benefit cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tc-benefits">
        {[
          { icon: Cpu, title: "PRODUCT INTELLIGENCE", body: "Exact specifications, datasheets, compatibility and warranty information." },
          { icon: Wallet, title: "YOUR PRICING", body: "Tenant-specific pricing, contract rates and approved commercial terms." },
          { icon: Workflow, title: "ONE PROCUREMENT FLOW", body: "From quotation and PO to ordering, delivery and invoicing." },
          { icon: Recycle, title: "FULL LIFECYCLE", body: "Order, deploy, support, recover, redeploy and recycle." },
        ].map((c) => (
          <div key={c.title} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-300 hover:shadow-sm transition-all">
            <div className="h-9 w-9 rounded-lg bg-slate-900 text-white grid place-items-center mb-3"><c.icon className="h-4.5 w-4.5" strokeWidth={1.7} /></div>
            <div className="text-[11px] font-semibold tracking-[0.08em] text-slate-900">{c.title}</div>
            <p className="text-[12px] text-slate-500 mt-1.5 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>

      {/* Product intelligence banner */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-6 py-5" data-testid="tc-intel-banner">
        <h2 className="font-heading font-semibold text-slate-900 tracking-tight text-lg">KNOW EXACTLY WHAT YOU'RE BUYING.</h2>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Specifications, compatibility, warranty, datasheets and accessories — all connected to the exact product you are viewing.
        </p>
      </div>

      <div className="text-[12px] text-slate-500">{data.total} products</div>

      {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {data.items.map((p) => (
            <div key={p.product_code} data-testid={`tc-card-${p.product_code}`} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md hover:border-slate-300 transition-all flex flex-col group relative">
              <button onClick={() => toggleWishlist(p.product_code)} data-testid={`tc-wish-${p.product_code}`} title="Save"
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 border border-slate-100 flex items-center justify-center z-10">
                <Heart className={"h-3.5 w-3.5 " + (wishlist.includes(p.product_code) ? "fill-rose-500 text-rose-500" : "text-slate-400")} />
              </button>
              <button onClick={() => setDetail(p.product_code)} className="text-left flex-1">
                <div className="h-28 flex items-center justify-center mb-2">
                  {p.primary_image ? <img src={p.primary_image} alt="" className="max-h-28 object-contain" /> : <PackageSearch className="h-10 w-10 text-slate-200" />}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">{p.brand}</div>
                <div className="text-sm font-medium text-slate-900 line-clamp-2 min-h-[2.5rem]">{p.product_name}</div>
                <div className="text-[11px] font-mono text-slate-400 mt-0.5 truncate">{p.oem_part_number || p.sku || p.product_code}</div>
                <div className="mt-2 flex items-center gap-1.5"><PriceTag pricing={p.pricing} />{p.is_standard && <Badge className="bg-indigo-50 text-indigo-700 text-[10px]" data-testid={`tc-standard-${p.product_code}`}>Standard</Badge>}</div>
                {p.availability && <div className={"mt-1 text-[10px] " + (p.availability.status === "IN_STOCK" ? "text-emerald-600" : p.availability.status === "LIMITED" ? "text-amber-600" : "text-slate-400")} data-testid={`tc-avail-${p.product_code}`}>{p.availability.label}</div>}
                {p.has_intelligence && <div className="mt-1 text-[10px] text-emerald-600 flex items-center gap-1"><Cpu className="h-3 w-3" />Full specifications</div>}
              </button>
              <div className="mt-3 flex items-center gap-1.5">
                <Button size="sm" onClick={() => addToCart(p.product_code)} data-testid={`tc-add-${p.product_code}`} className="flex-1 h-8 bg-sky-600 hover:bg-sky-700 text-white"><ShoppingCart className="h-3.5 w-3.5 mr-1" />Add</Button>
                <button onClick={() => toggleCompare(p.product_code)} data-testid={`tc-compare-${p.product_code}`} title="Compare"
                  className={"h-8 w-8 rounded-md border flex items-center justify-center " + (compare.includes(p.product_code) ? "bg-sky-600 text-white border-sky-600" : "border-slate-200 text-slate-500")}>
                  {compare.includes(p.product_code) ? <Check className="h-4 w-4" /> : <Scale className="h-4 w-4" />}
                </button>
              </div>
            </div>
          ))}
          {data.items.length === 0 && <div className="col-span-full text-center py-16 text-slate-400 text-sm">No products match your search.</div>}
        </div>
      )}

      {/* Footer brand message */}
      <div className="mt-4 border-t border-slate-200 pt-6 pb-2 text-center" data-testid="tc-footer-brand">
        <div className="font-heading font-semibold tracking-[0.14em] text-slate-900 text-sm">SUNTRONIX</div>
        <div className="text-[12px] text-slate-500 mt-0.5">Enterprise Technology &amp; Device Lifecycle Partner</div>
        <div className="text-[11px] text-slate-400 mt-1 tracking-wide">One Partner. Complete Lifecycle.</div>
      </div>

      {/* Cart drawer */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="tc-cart-drawer">
          <SheetHeader><SheetTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-sky-600" />My Cart</SheetTitle></SheetHeader>
          <div className="flex-1 overflow-y-auto -mx-2 px-2 py-3 space-y-2">
            {(!cart || cart.items.length === 0) && <div className="text-center py-16 text-slate-400 text-sm">Your cart is empty.</div>}
            {cart?.items.map((it) => (
              <div key={it.product_code} data-testid={`cart-line-${it.product_code}`} className="flex gap-2 rounded-lg border border-slate-200 p-2">
                {it.primary_image ? <img src={it.primary_image} alt="" className="w-14 h-14 object-contain" /> : <div className="w-14 h-14 bg-slate-50 rounded flex items-center justify-center"><PackageSearch className="h-5 w-5 text-slate-300" /></div>}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-slate-800 line-clamp-2">{it.product_name}</div>
                  <div className="text-[10px] font-mono text-slate-400">{it.part_number || it.product_code}</div>
                  {it.availability && <div className={"text-[10px] " + (it.availability.status === "IN_STOCK" ? "text-emerald-600" : "text-slate-400")}>{it.availability.label}</div>}
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateQty(it.product_code, it.quantity - 1)} className="h-6 w-6 rounded border border-slate-200 flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                      <span className="text-[13px] w-6 text-center" data-testid={`cart-qty-${it.product_code}`}>{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_code, it.quantity + 1)} className="h-6 w-6 rounded border border-slate-200 flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                    </div>
                    <div className="text-[13px] font-semibold text-slate-800">{it.pricing?.mode === "PRICE" ? money(it.line_total, cart.currency) : <span className="text-[11px] text-amber-600">RFQ</span>}</div>
                    <button onClick={() => removeItem(it.product_code)} data-testid={`cart-remove-${it.product_code}`} className="text-rose-400 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {cart && cart.items.length > 0 && (
            <div className="border-t border-slate-100 pt-3 space-y-2">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-semibold text-slate-900" data-testid="cart-subtotal">{money(cart.subtotal, cart.currency)}</span></div>
              {cart.rfq_lines > 0 && <div className="text-[11px] text-amber-600">{cart.rfq_lines} item(s) need a quote — pricing confirmed on quotation.</div>}
              <p className="text-[11px] text-slate-400">Taxes computed on the formal quotation. Delivery at actuals.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={saveCurrentCart} data-testid="cart-save" className="flex-1"><Bookmark className="h-4 w-4 mr-1.5" />Save cart</Button>
                <Button onClick={() => { setCartOpen(false); setShowRfq(true); }} data-testid="cart-request-quote" className="flex-1 bg-sky-600 hover:bg-sky-700 text-white"><Send className="h-4 w-4 mr-1.5" />Request Quote</Button>
              </div>
              {cart.direct_order_enabled && cart.rfq_lines === 0 && (
                <Button onClick={() => { setCartOpen(false); setShowCheckout(true); }} data-testid="cart-place-order" className="w-full bg-slate-900 hover:bg-slate-800 text-white"><ShoppingCart className="h-4 w-4 mr-1.5" />Place Order</Button>
              )}
            </div>
          )}
          {savedCarts.length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <button onClick={() => setShowSaved((v) => !v)} className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-600 mb-1" data-testid="cart-saved-toggle">
                <Bookmark className="h-3.5 w-3.5" />Saved carts ({savedCarts.length}){showSaved ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
              </button>
              {showSaved && (
                <div className="space-y-1.5">
                  {savedCarts.map((sc) => (
                    <div key={sc.id} data-testid={`saved-cart-${sc.id}`} className="flex items-center gap-2 text-[12px] rounded-md border border-slate-100 px-2 py-1.5">
                      <div className="flex-1 min-w-0"><div className="font-medium text-slate-700 truncate">{sc.name}</div><div className="text-[10px] text-slate-400">{sc.item_count} item(s)</div></div>
                      <button onClick={() => restoreCart(sc.id)} data-testid={`saved-cart-restore-${sc.id}`} className="text-sky-600 flex items-center gap-0.5"><RotateCcw className="h-3 w-3" />Restore</button>
                      <button onClick={() => deleteSavedCart(sc.id)} className="text-rose-400"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {detail && <ProductDetail code={detail} onClose={() => setDetail(null)} onAdd={addToCart} />}
      {showCompare && <CompareDialog codes={compare} onClose={() => setShowCompare(false)} />}
      {showQuick && <QuickOrderDialog onClose={() => setShowQuick(false)} onDone={(c) => setCart(c)} />}
      {showPo && <PoImportDialog onClose={() => setShowPo(false)} onDone={(c) => { if (c) setCart(c); loadCart(); }} />}
      {showRfq && <RfqDialog onClose={() => setShowRfq(false)} onDone={() => { setShowRfq(false); loadCart(); toast.success("Check My Quotes for your request."); }} />}
      {showCheckout && <CheckoutDialog poRequired={settings?.po_required} onClose={() => setShowCheckout(false)} onDone={() => { setShowCheckout(false); loadCart(); }} />}
    </div>
  );
}
